/**
 * Demo data for teams (?mock=1): the people from the auth demo, two teams, owners and rotas
 * on the demo projects, routed and escalated items, and someone else looking at a session.
 */
import { ALL_PROJECTS_WORKSPACE_ID, DEFAULT_ORG_ID, defaultRoutingSettings } from '@orgus/protocol';
import type { TeamsState } from '@orgus/protocol';
import { MOCK_IDS } from '../../lib/mock.ts';

const MIN = 60_000;
const H = 60 * MIN;

/** Start of today, 09:00 local, so the demo rota changes hands at a believable time. */
function nineToday(now: number): number {
  const d = new Date(now);
  d.setHours(9, 0, 0, 0);
  return d.getTime() > now ? d.getTime() - 24 * H : d.getTime();
}

export function createDemoTeams(now: number): TeamsState {
  const nine = nineToday(now);
  const P = MOCK_IDS;
  return {
    rev: 1,
    orgs: [{ id: DEFAULT_ORG_ID, name: 'Qawl Labs', createdAt: now - 30 * 24 * H }],
    workspaces: [
      { id: ALL_PROJECTS_WORKSPACE_ID, orgId: DEFAULT_ORG_ID, name: 'All projects', projectIds: null, builtIn: true, createdAt: now - 30 * 24 * H, createdBy: null },
      { id: 'wsp_core', orgId: DEFAULT_ORG_ID, name: 'Core platform', projectIds: [P.qawl, P.orgus], builtIn: false, createdAt: now - 9 * 24 * H, createdBy: 'srikanth' },
      { id: 'wsp_billing', orgId: DEFAULT_ORG_ID, name: 'Billing', projectIds: [P.billing], builtIn: false, createdAt: now - 4 * 24 * H, createdBy: 'maya-chen' },
    ],
    teams: [
      { id: 'team_platform', orgId: DEFAULT_ORG_ID, name: 'Platform', handle: 'platform', members: ['srikanth', 'devon'], createdAt: now - 20 * 24 * H, createdBy: 'srikanth' },
      { id: 'team_billing', orgId: DEFAULT_ORG_ID, name: 'Billing', handle: 'billing', members: ['maya-chen', 'devon'], createdAt: now - 12 * 24 * H, createdBy: 'maya-chen' },
      { id: 'team_docs', orgId: DEFAULT_ORG_ID, name: 'Docs crew', handle: 'docs', members: ['priya-r'], createdAt: now - 2 * 24 * H, createdBy: 'maya-chen' },
    ],
    ownership: [
      { projectId: P.qawl, owners: ['srikanth'], rota: { members: ['devon', 'srikanth'], shiftHours: 24, startsAt: nine, overrides: [] }, updatedAt: now - 3 * 24 * H, updatedBy: 'srikanth' },
      {
        projectId: P.billing,
        owners: ['@billing'],
        rota: { members: ['maya-chen', 'devon'], shiftHours: 168, startsAt: nine - 2 * 24 * H, overrides: [{ login: 'devon', from: now - 2 * H, to: now + 3 * H, note: 'Maya at the dentist' }] },
        updatedAt: now - 2 * H,
        updatedBy: 'maya-chen',
      },
      { projectId: P.orgus, owners: ['@platform'], rota: null, updatedAt: now - 8 * 24 * H, updatedBy: 'srikanth' },
    ],
    routing: { ...defaultRoutingSettings(), escalateAfterMinutes: 10 },
    assignments: [
      {
        key: 's:c3d8a7f1-request-card',
        refType: 'session',
        title: 'Wire the request card into chat and add one inbox for agent requests',
        projectId: P.qawl,
        assignees: ['devon'],
        by: 'routing',
        at: now - 3 * MIN,
        reason: 'routed',
        level: 0,
        ruleId: 'rte_duty',
        why: 'On duty for this project. Rule: Everything else goes to whoever is on duty.',
      },
      {
        key: 's:9d1a4c6e-webhook-tests',
        refType: 'session',
        title: 'Add integration tests for webhook retries',
        projectId: P.billing,
        assignees: ['devon', 'maya-chen'],
        by: 'routing',
        at: now - 2 * MIN,
        reason: 'escalated',
        level: 1,
        ruleId: 'rte_duty',
        why: 'Nobody answered in 10 minutes, so it went to the next on call.',
      },
      {
        key: 'w:c-docs-mbp',
        refType: 'worktree',
        title: 'Uncommitted changes in docs-site',
        projectId: P.docs,
        assignees: ['priya-r'],
        by: 'srikanth',
        at: now - 40 * MIN,
        reason: 'mention',
        level: 0,
        why: 'Assigned by @srikanth',
        note: 'Can you check these before the v2 launch?',
      },
    ],
    presence: {
      'session:c3d8a7f1-request-card': [{ login: 'maya-chen', name: 'Maya Chen', since: now - 4 * MIN, at: now - 5_000, tabs: 1 }],
      'flow:b1f4c2e0-approvals-w1': [
        { login: 'devon', name: 'Devon Ortiz', since: now - 6 * MIN, at: now - 8_000, tabs: 1 },
        { login: 'maya-chen', name: 'Maya Chen', since: now - 2 * MIN, at: now - 3_000, tabs: 2 },
      ],
    },
    people: [
      { login: 'owner', name: 'Owner (sign-in link)', role: 'owner' },
      { login: 'srikanth', name: 'Srikanth Vemulapalli', role: 'owner' },
      { login: 'maya-chen', name: 'Maya Chen', role: 'admin' },
      { login: 'devon', name: 'Devon Ortiz', role: 'operator' },
      { login: 'priya-r', name: 'Priya Raman', role: 'viewer' },
    ],
    sessionOwners: { 'c3d8a7f1-request-card': 'srikanth', 'f0c9e1a2-sessions-page': 'srikanth', '9d1a4c6e-webhook-tests': 'maya-chen' },
  };
}
